import { KeyboardArrowDown } from "@mui/icons-material";
import { FormControl, MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import React from "react";

export type QuantitySelectVariant = 'basket' | 'order';

export type QuantitySelectProps = {
    value: number;
    onChange: (quantity: number) => void;
    minQuantity?: number;
    maxQuantity?: number;
    disabled?: boolean;
    ariaLabel?: string;
    variant?: QuantitySelectVariant;
    placeholder?: string;
    showPlaceholderOption?: boolean;
    isOptionDisabled?: (quantity: number) => boolean;
    id?: string;
    className?: string;
};

export default function QuantitySelect({
    value,
    onChange,
    minQuantity = 0,
    maxQuantity = 9,
    disabled,
    ariaLabel,
    variant = 'order',
    placeholder,
    showPlaceholderOption,
    isOptionDisabled,
    id,
    className,
}: QuantitySelectProps) {
    const options = Array.from({ length: maxQuantity - minQuantity + 1 }, (_, index) => minQuantity + index);
    const showPlaceholder = showPlaceholderOption && !value;
    const selectValue = showPlaceholder ? '' : value;

    const onSelectChange = (event: SelectChangeEvent<number | string>) => {
        const raw = event.target.value;
        if (raw === '') return;
        onChange(+raw);
    };

    return (
        <FormControl className={`quantity-select quantity-select--${variant} ${className ?? ''} ${showPlaceholder ? 'quantity-select--placeholder' : ''}`} size="small" disabled={disabled}>
            <Select
                id={id}
                className={`quantity-select__input quantity-select__input--${variant}`}
                value={selectValue}
                onChange={onSelectChange}
                displayEmpty={showPlaceholderOption}
                slotProps={{ input: { 'aria-label': ariaLabel } }}
                IconComponent={KeyboardArrowDown}
                MenuProps={{
                    disableScrollLock: true,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    slotProps: { paper: { className: `quantity-select-menu quantity-select-menu--${variant}` } },
                }}
            >
                {showPlaceholderOption && (
                    <MenuItem value="" disabled>{placeholder}</MenuItem>
                )}
                {options.map(optionQuantity => (
                    <MenuItem key={optionQuantity} value={optionQuantity} disabled={isOptionDisabled?.(optionQuantity)}>
                        {optionQuantity}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
